import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLang, tx } from "@/context/LangContext";
import { T } from "@/data/content";
import { MessageCircle, X, SendHorizontal } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatWidget() {
    const { lang } = useLang();
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [streaming, setStreaming] = useState(false);
    const [sessionId] = useState(() =>
        typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `s-${Date.now()}`
    );
    const scrollRef = useRef(null);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
    }, [messages, streaming]);

    useEffect(() => {
        if (open) window.__lenis?.stop?.();
        else window.__lenis?.start?.();
    }, [open]);

    const send = async () => {
        const text = input.trim();
        if (!text || streaming) return;
        setInput("");
        setMessages((ms) => [...ms, { role: "user", content: text }, { role: "assistant", content: "" }]);
        setStreaming(true);
        let raw = "";
        try {
            const res = await fetch(`${API}/chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ session_id: sessionId, message: text, lang }),
            });
            const reader = res.body.getReader();
            const dec = new TextDecoder();
            let buf = "";
            for (;;) {
                const { done, value } = await reader.read();
                if (done) break;
                buf += dec.decode(value, { stream: true });
                const parts = buf.split("\n\n");
                buf = parts.pop();
                for (const line of parts) {
                    if (!line.startsWith("data: ")) continue;
                    const ev = JSON.parse(line.slice(6));
                    if (ev.type === "delta" || ev.type === "error") {
                        raw += ev.content;
                        // hide the navigation token (complete or partially streamed)
                        const display = raw
                            .replace(/\[\[NAV:[a-z]*\]\]/gi, "")
                            .replace(/\[\[?\s*N?A?V?:?[a-z]*$/i, "");
                        setMessages((ms) => {
                            const c = [...ms];
                            c[c.length - 1] = { role: "assistant", content: display };
                            return c;
                        });
                    }
                }
            }
            // l'IA propose une redirection → on y va en une fraction de seconde
            const nav = raw.match(/\[\[NAV:(home|videos|photo|contact|radio|portal)\]\]/i);
            if (nav) {
                setTimeout(() => window.__goPage?.(nav[1].toLowerCase()), 350);
            }
        } catch {
            setMessages((ms) => {
                const c = [...ms];
                c[c.length - 1] = {
                    role: "assistant",
                    content:
                        lang === "fr"
                            ? "Connexion interrompue — réessayez dans un instant."
                            : "Connection lost — please try again in a moment.",
                };
                return c;
            });
        } finally {
            setStreaming(false);
        }
    };

    const greeting =
        lang === "fr"
            ? "Bonjour — posez vos questions sur les services, les projets ou les disponibilités de Casper."
            : "Hi — ask me anything about Casper's services, work or availability.";

    return (
        <>
            <button
                data-testid="chat-open-btn"
                onClick={() => setOpen(!open)}
                aria-label={open ? "Close chat" : "Open chat"}
                className={`fixed bottom-5 right-5 z-[85] flex h-14 w-14 items-center justify-center rounded-full border shadow-xl transition-all duration-300 md:bottom-8 md:right-8 ${
                    open
                        ? "border-cream/40 bg-ink text-cream"
                        : "border-cream/20 bg-cream text-ink hover:bg-violet hover:border-violet hover:text-cream"
                }`}
            >
                {open ? <X size={20} /> : <MessageCircle size={20} />}
                {!open && (
                    <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-ink bg-emerald" />
                )}
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="chat-widget"
                        initial={{ opacity: 0, y: 24, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 24, scale: 0.96 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        className="fixed bottom-24 right-5 z-[85] flex h-[540px] max-h-[72vh] w-[min(92vw,380px)] origin-bottom-right flex-col border border-white/15 bg-ink2/95 backdrop-blur-xl md:bottom-26 md:right-8"
                    >
                        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
                            <div>
                                <p className="font-display text-sm font-extrabold uppercase tracking-[0.15em] text-cream">
                                    Gloom Assistant
                                </p>
                                <p className="mt-0.5 flex items-center gap-2 font-mono text-[9px] tracking-[0.3em] text-cream/40">
                                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald" />
                                    {tx(lang, T.chat.online)}
                                </p>
                            </div>
                            <button
                                data-testid="chat-close-btn"
                                onClick={() => setOpen(false)}
                                aria-label="Close"
                                className="flex h-8 w-8 items-center justify-center rounded-full border border-white/15 text-cream/70 transition-colors hover:border-violet hover:text-cream"
                            >
                                <X size={14} />
                            </button>
                        </div>

                        <div
                            ref={scrollRef}
                            data-testid="chat-messages"
                            className="flex-1 space-y-3 overflow-y-auto px-4 py-4"
                        >
                            <div className="flex justify-start">
                                <p className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-bl-sm border border-white/10 bg-white/[0.06] px-4 py-2.5 text-sm font-light leading-relaxed text-cream/90">
                                    {greeting}
                                </p>
                            </div>
                            {messages.map((m, i) => (
                                <div
                                    key={i}
                                    data-testid={`chat-message-${i}`}
                                    className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                                >
                                    <p
                                        className={`max-w-[85%] whitespace-pre-wrap px-4 py-2.5 text-sm font-light leading-relaxed ${
                                            m.role === "user"
                                                ? "rounded-2xl rounded-br-sm bg-violet/90 text-cream"
                                                : "rounded-2xl rounded-bl-sm border border-white/10 bg-white/[0.06] text-cream/90"
                                        } ${m.role === "assistant" && !m.content ? "py-3" : ""}`}
                                    >
                                        {m.content || (streaming && i === messages.length - 1 ? "•••" : "")}
                                    </p>
                                </div>
                            ))}
                        </div>

                        <div className="flex items-center gap-2 border-t border-white/10 p-3">
                            <input
                                data-testid="chat-input"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && send()}
                                placeholder={tx(lang, T.chat.inputPh)}
                                className="flex-1 rounded-full border border-white/10 bg-white/5 px-4 py-2.5 text-sm font-light text-cream outline-none transition-colors placeholder:text-cream/30 focus:border-violet"
                            />
                            <button
                                data-testid="chat-send-btn"
                                onClick={send}
                                disabled={streaming || !input.trim()}
                                aria-label={tx(lang, T.chat.send)}
                                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cream text-ink transition-colors duration-300 hover:bg-violet hover:text-cream disabled:opacity-40"
                            >
                                <SendHorizontal size={15} />
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
